import { NavTabPath, PagePath, SectionPath, QuestionPath, NavTabInfo, PageInfo, SectionInfo, QuestionInfo, FormlyFieldConfig, ConfigDataParser } from './config-data-parser';

// These types could be made a bit narrower than just "any".
// However the type definition would end up being so complex that is probably not worth the effort.
export type FormModel = any;
export type NavTabModel = any;
export type PageModel = any;
export type SectionModel = any;
export type QuestionModel = any;

export type NavTabItem = { navTabModel: NavTabModel };
export type PageItem = { pageModel: PageModel, pageIndex: number };
export type SectionItem = { sectionModel: SectionModel, pageIndex: number, sectionIndex: number };
export type QuestionItem = { questionModel: QuestionModel, pageIndex: number, sectionIndex: number };

export class FormModelParser {

  // These methods return the model of a single item (or null/undefined value if not found).
  // If pageIndex/sectionIndex is null/undefined, then it is assumed that the page/section is non-repeating.

  static getNavTabModel(formModel: FormModel, navTabPath: NavTabPath): NavTabModel {

    const [navTabNumber] = navTabPath;

    const navTabModel: NavTabModel = formModel?.[`form`]?.[`tabs`]?.[`tab${navTabNumber}`];
    return navTabModel;
  }

  static getPageModel(formModel: FormModel, pagePath: PagePath, pageIndex: number): PageModel {

    const [navTabNumber, pageNumber] = pagePath;
    const navTabPath: NavTabPath = [navTabNumber];
    const isTryingToFindRepeatingPageItem = pageIndex !== null && pageIndex !== undefined;

    const pageModel: PageModel = isTryingToFindRepeatingPageItem
      ? this.getNavTabModel(formModel, navTabPath)?.[`page${pageNumber}`]?.[pageIndex]
      : this.getNavTabModel(formModel, navTabPath)?.[`page${pageNumber}`];
    return pageModel;
  }

  static getSectionModel(formModel: FormModel, sectionPath: SectionPath, pageIndex: number, sectionIndex: number): SectionModel {

    const [navTabNumber, pageNumber, sectionCode] = sectionPath;
    const pagePath: PagePath = [navTabNumber, pageNumber];
    const isTryingToFindRepeatingSectionItem = sectionIndex !== null && sectionIndex !== undefined;

    const sectionModel: SectionModel = isTryingToFindRepeatingSectionItem
      ? this.getPageModel(formModel, pagePath, pageIndex)?.[sectionCode]?.[sectionIndex]
      : this.getPageModel(formModel, pagePath, pageIndex)?.[sectionCode];
    return sectionModel;
  }

  static getQuestionModel(formModel: FormModel, questionPath: QuestionPath, pageIndex: number, sectionIndex: number): QuestionModel {

    const [navTabNumber, pageNumber, sectionCode, questionCode] = questionPath;
    const sectionPath: SectionPath = [navTabNumber, pageNumber, sectionCode];

    const questionModel: QuestionModel = this.getSectionModel(formModel, sectionPath, pageIndex, sectionIndex)?.[questionCode];
    return questionModel;
  }

  // These methods return the models of all the items (or an empty array if not found).
  // If the page/section is non-repeating, then only up to one item is returned.
  // If the page/section is repeating, then all the items are returned.

  static getNavTabItems(formModel: FormModel, navTabInfo: NavTabInfo): NavTabItem[] {

    const { navTabPath } = navTabInfo;

    let navTabItems: NavTabItem[] = [];
    const navTabModel = this.getNavTabModel(formModel, navTabPath);
    if (navTabModel) {
      const navTabItem: NavTabItem = { navTabModel };
      navTabItems.push(navTabItem);
    }
    return navTabItems;
  }

  static getPageItems(formModel: FormModel, pageInfo: PageInfo): PageItem[] {

    const { pagePath, isRepeatingPage } = pageInfo;
    const [navTabNumber] = pagePath;
    const navTabPath: NavTabPath = [navTabNumber];
    const navTabInfo: NavTabInfo = { navTabPath };

    let pageItems: PageItem[] = [];
    const navTabItems = this.getNavTabItems(formModel, navTabInfo);
    for (const navTabItem of navTabItems) {
      if (!isRepeatingPage) {
        const pageIndex: number = null;
        const pageModel = this.getPageModel(formModel, pagePath, pageIndex);
        if (pageModel) {
          const pageItem: PageItem = { pageModel, pageIndex };
          pageItems.push(pageItem);
        }
      }
      else {
        for (let pageIndex = 0; ; pageIndex++) {
          const pageModel = this.getPageModel(formModel, pagePath, pageIndex);
          if (!pageModel) {
            break;
          }
          const pageItem: PageItem = { pageModel, pageIndex };
          pageItems.push(pageItem);
        }
      }
    }
    return pageItems;
  }

  static getSectionItems(formModel: FormModel, sectionInfo: SectionInfo): SectionItem[] {

    const { sectionPath, isRepeatingPage, isRepeatingSection } = sectionInfo;
    const [navTabNumber, pageNumber] = sectionPath;
    const pagePath: PagePath = [navTabNumber, pageNumber];
    const pageInfo: PageInfo = { pagePath, isRepeatingPage };

    let sectionItems: SectionItem[] = [];
    const pageItems = this.getPageItems(formModel, pageInfo);
    for (const pageItem of pageItems) {
      const { pageIndex } = pageItem;
      if (!isRepeatingSection) {
        const sectionIndex: number = null;
        const sectionModel = this.getSectionModel(formModel, sectionPath, pageIndex, sectionIndex);
        if (sectionModel) {
          const sectionItem: SectionItem = { sectionModel, pageIndex, sectionIndex };
          sectionItems.push(sectionItem);
        }
      }
      else {
        for (let sectionIndex = 0; ; sectionIndex++) {
          const sectionModel = this.getSectionModel(formModel, sectionPath, pageIndex, sectionIndex);
          if (!sectionModel) {
            break;
          }
          const sectionItem: SectionItem = { sectionModel, pageIndex, sectionIndex };
          sectionItems.push(sectionItem);
        }
      }
    }
    return sectionItems;
  }

  static getQuestionItems(formModel: FormModel, questionInfo: QuestionInfo): QuestionItem[] {

    const { questionPath, isRepeatingPage, isRepeatingSection } = questionInfo;
    const [navTabNumber, pageNumber, sectionCode] = questionPath;
    const sectionPath: SectionPath = [navTabNumber, pageNumber, sectionCode];
    const sectionInfo: SectionInfo = { sectionPath, isRepeatingPage, isRepeatingSection };

    let questionItems: QuestionItem[] = [];
    const sectionItems = this.getSectionItems(formModel, sectionInfo);
    for (const sectionItem of sectionItems) {
      const { pageIndex, sectionIndex } = sectionItem;
      const questionModel = this.getQuestionModel(formModel, questionPath, pageIndex, sectionIndex);
      if (!questionModel) {
        break;
      }
      const questionItem: QuestionItem = { questionModel, pageIndex, sectionIndex };
      questionItems.push(questionItem);
    }
    return questionItems;
  }

  // These methods set the model of a single item.
  // If pageIndex/sectionIndex is null/undefined, then it is assumed that the page/section is non-repeating.
  // If the parent node does not exist, the item is not set.

  static setNavTabModel(formModel: FormModel, navTabPath: NavTabPath, newNavTabModel: NavTabModel): boolean {

    const [navTabNumber] = navTabPath;
    const navTabKey = `tab${navTabNumber}`;

    const navTabParentModel = formModel?.form?.tabs;
    if (navTabParentModel) {
      navTabParentModel[navTabKey] = newNavTabModel;
      return true;
    }
    return false;
  }

  static setPageModel(formModel: FormModel, pagePath: PagePath, pageIndex: number, newPageModel: PageModel): boolean {

    const [navTabNumber, pageNumber] = pagePath;
    const navTabPath: NavTabPath = [navTabNumber];
    const pageKey = `page${pageNumber}`;
    const isRepeatingPageItem = pageIndex !== null && pageIndex !== undefined;

    const pageParentModel = this.getNavTabModel(formModel, navTabPath);
    if (pageParentModel) {
      if (isRepeatingPageItem) {
        const pageItemParentModel = pageParentModel[pageKey];
        if (pageItemParentModel) {
          pageItemParentModel[pageIndex] = newPageModel;
          return true;
        }
      }
      else {
        pageParentModel[pageKey] = newPageModel;
        return true;
      }
    }
    return false;
  }

  static setSectionModel(formModel: FormModel, sectionPath: SectionPath, pageIndex: number, sectionIndex: number, newSectionModel: SectionModel): boolean {

    const [navTabNumber, pageNumber, sectionCode] = sectionPath;
    const pagePath: PagePath = [navTabNumber, pageNumber];
    const isRepeatingSectionItem = sectionIndex !== null && sectionIndex !== undefined;

    const sectionParentModel = this.getPageModel(formModel, pagePath, pageIndex);
    if (sectionParentModel) {
      if (isRepeatingSectionItem) {
        const sectionItemParentModel = sectionParentModel[sectionCode];
        if (sectionItemParentModel) {
          sectionItemParentModel[sectionIndex] = newSectionModel;
          return true;
        }
      }
      else {
        sectionParentModel[sectionCode] = newSectionModel;
        return true;
      }
    }
    return false;
  }

  static setQuestionModel(formModel: FormModel, questionPath: QuestionPath, pageIndex: number, sectionIndex: number, newQuestionModel: QuestionModel): boolean {

    const [navTabNumber, pageNumber, sectionCode, questionCode] = questionPath;
    const sectionPath: SectionPath = [navTabNumber, pageNumber, sectionCode];

    const questionParentModel = this.getSectionModel(formModel, sectionPath, pageIndex, sectionIndex);
    if (questionParentModel) {
      questionParentModel[questionCode] = newQuestionModel;
      return true;
    }
    return false;
  }

  // These methods set the model of a single item.
  // If pageIndex/sectionIndex is null/undefined, then it is assumed that the page/section is non-repeating.
  // If any of the parent nodes is nullish, it is created, so that the item is always guaranteed to be set.

  static ensureFormModel(formModel: FormModel): FormModel {

    formModel ??= {};
    formModel.form ??= {};
    formModel.form.tabs ??= {};

    return formModel;
  }

  static ensureNavTabModel(formModel: FormModel, navTabPath: NavTabPath, newNavTabModel: NavTabModel): FormModel {

    const [navTabNumber] = navTabPath;
    const navTabKey = `tab${navTabNumber}`;

    formModel = this.ensureFormModel(formModel);
    formModel.form.tabs[navTabKey] = newNavTabModel;

    return formModel;
  }

  static ensurePageModel(formModel: FormModel, pagePath: PagePath, pageIndex: number, newPageModel: PageModel): FormModel {

    const [navTabNumber, pageNumber] = pagePath;
    const navTabPath: NavTabPath = [navTabNumber];
    const pageKey = `page${pageNumber}`;
    const isRepeatingPageItem = pageIndex !== null && pageIndex !== undefined;

    const navTabModel = this.getNavTabModel(formModel, navTabPath) ?? {};
    this.ensureNavTabModel(formModel, navTabPath, navTabModel);

    if (isRepeatingPageItem) {
      navTabModel[pageKey] ??= [];
      navTabModel[pageKey][pageIndex] = newPageModel;
    }
    else {
      navTabModel[pageKey] = newPageModel;
    }

    return formModel;
  }

  static ensureSectionModel(formModel: FormModel, sectionPath: SectionPath, pageIndex: number, sectionIndex: number, newSectionModel: SectionModel): FormModel {

    const [navTabNumber, pageNumber, sectionCode] = sectionPath;
    const pagePath: PagePath = [navTabNumber, pageNumber];
    const isRepeatingSectionItem = sectionIndex !== null && sectionIndex !== undefined;

    const pageModel = this.getPageModel(formModel, pagePath, pageIndex) ?? {};
    this.ensurePageModel(formModel, pagePath, pageIndex, pageModel);

    if (isRepeatingSectionItem) {
      pageModel[sectionCode] ??= [];
      pageModel[sectionCode][pageIndex] = newSectionModel;
    }
    else {
      pageModel[sectionCode] = newSectionModel;
    }

    return formModel;
  }

  static ensureQuestionModel(formModel: FormModel, questionPath: QuestionPath, pageIndex: number, sectionIndex: number, newQuestionModel: QuestionModel): FormModel {

    const [navTabNumber, pageNumber, sectionCode, questionCode] = questionPath;
    const sectionPath: SectionPath = [navTabNumber, pageNumber, sectionCode];

    const sectionModel = this.getSectionModel(formModel, sectionPath, pageIndex, sectionIndex) ?? {};
    this.ensureSectionModel(formModel, sectionPath, pageIndex, sectionIndex, sectionModel);

    sectionModel[questionCode] = newQuestionModel;

    return formModel;
  }

  // Other miscellaneous methods

  static modelHasQuestions(formModel: FormModel, formConfig: FormlyFieldConfig[]): boolean {
    const questionPaths = ConfigDataParser.getQuestionPaths(formConfig);
    for (const questionPath of questionPaths) {
      const questionConfig = ConfigDataParser.getQuestionConfig(formConfig, questionPath);
      const { isRepeatingPage, isRepeatingSection } = questionConfig.props;
      const questionInfo: QuestionInfo = { questionPath, isRepeatingPage, isRepeatingSection };
      const questionItems = this.getQuestionItems(formModel, questionInfo);
      if (questionItems.length) {
        return true;
      }
    }
    return false;
  }
}
